import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap';
import { ToasterService } from 'angular2-toaster';
import * as moment from 'moment';
import * as XLSX from 'xlsx';
import swal from 'sweetalert2';

import { DoctorIncentiveTestingService, DoctorService } from '../../../services/index';

@Component({
    selector: 'add-edit-doctor-incentive-testing',
    templateUrl: './addEditDoctorIncentiveTesting.component.html'
})
export class AddEditDoctorIncentiveTestingComponent implements OnInit {

    @Output() doctorIncentiveTestingLoadEvent = new EventEmitter();

    public title: string;
    public doctorIncentiveTestingData: any = {};
    public isLoading: boolean = false;

    statusOptions = ['Active', 'Inactive'];
    allDoctors: any[] = [];
    filteredDoctors: any[] = [];
    doctorSearch: string = '';

    @ViewChild('fileUpload') fileUpload;
    isExcelUpload: boolean = false;
    excelData: any;
    rawFileName: string = '';
    incentiveUploadList: any[] = [];

    constructor(
        public bsModalRef: BsModalRef,
        public toasterService: ToasterService,
        private doctorIncentiveTestingService: DoctorIncentiveTestingService,
        private doctorService: DoctorService
    ) { }

    ngOnInit(): void {
        if (this.doctorIncentiveTestingData.mode === 'Add') {
            this.doctorIncentiveTestingData.status = 'Active';
            this.doctorIncentiveTestingData.incentive_percentage = 0;
        } else {
            this.doctorSearch = this.doctorIncentiveTestingData.doctor_name || '';
        }
        this.loadDoctors();
    }

    loadDoctors(): void {
        this.doctorService.getDoctorList({ status: 'Active', start: 0, limit: 1000 })
            .then((res: any) => {
                this.allDoctors = res.data || res || [];
                this.filteredDoctors = this.allDoctors;
            });
    }

    onDoctorSearch(val: string): void {
        if (!val) {
            this.filteredDoctors = this.allDoctors;
            this.doctorIncentiveTestingData.ref_id = null;
            return;
        }
        this.filteredDoctors = this.allDoctors.filter(d =>
            d.name.toLowerCase().includes(val.toLowerCase())
        );
    }

    onDoctorSelect(event: any): void {
        const selected = this.allDoctors.find(d => d.name === event.option.value);
        if (selected) {
            this.doctorIncentiveTestingData.ref_id = selected.ref_id;
            this.doctorSearch = selected.name;
        }
    }

    onUploadModeChange(): void {
        if (this.isExcelUpload) {
            this.clearExcelData();
        }
    }

    downloadSampleFile(): void {
        const ws = XLSX.utils.aoa_to_sheet([
            ['DOCTOR NAME', 'INCENTIVE %', 'START DATE', 'END DATE', 'STATUS'],
            ['Dr. Sample Doctor', 10, '01-04-2026', '31-03-2027', 'Active']
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, 'Doctor_Incentive_Testing_Sample_' + moment().format('DD-MM-YYYY') + '.xlsx');
    }

    openFileWindow(): void {
        this.fileUpload.nativeElement.click();
    }

    uploadFile(event): void {
        if (!event.target.files || event.target.files.length === 0) { return; }
        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onload = (e: any) => {
            const wb = XLSX.read(e.target.result, { type: 'binary' });
            const json = wb.SheetNames.reduce((acc, name) => {
                acc[name] = XLSX.utils.sheet_to_json(wb.Sheets[name]);
                return acc;
            }, {});
            if (Object.keys(json).length !== 1) {
                swal({ title: 'ERROR', text: 'Please upload a file with only one sheet', type: 'error' });
                return;
            }
            this.incentiveUploadList = (json as any)['Sheet1'] || [];
            this.excelData = JSON.stringify(json);
            this.rawFileName = file.name;
        };
        reader.readAsBinaryString(file);
        this.fileUpload.nativeElement.value = '';
    }

    clearExcelData(): void {
        this.excelData = '';
        this.rawFileName = '';
        this.incentiveUploadList = [];
    }

    buildResultHtml(message: string, errors: string[]): string {
        let html = `<p style="margin-bottom:8px;">${message}</p>`;
        if (errors && errors.length > 0) {
            html += `<div style="max-height:300px;overflow-y:auto;text-align:left;border:1px solid #ddd;border-radius:4px;padding:6px 10px;">`;
            errors.forEach((err, i) => {
                html += `<div style="padding:5px 0;border-bottom:1px solid #eee;color:#dc3545;font-size:14px;">${i + 1}. ${err}</div>`;
            });
            html += `</div>`;
        }
        return html;
    }

    onSave(): void {
        if (this.isExcelUpload) {
            this.saveExcelFile();
        } else {
            this.saveManualEntry();
        }
    }

    saveManualEntry(): void {
        this.isLoading = true;

        let params: any = {
            mode: this.doctorIncentiveTestingData.mode,
            ref_id: this.doctorIncentiveTestingData.ref_id,
            incentive_percentage: this.doctorIncentiveTestingData.incentive_percentage,
            start_date: this.doctorIncentiveTestingData.start_date
                ? moment(this.doctorIncentiveTestingData.start_date).format('YYYY-MM-DD')
                : '',
            end_date: this.doctorIncentiveTestingData.end_date
                ? moment(this.doctorIncentiveTestingData.end_date).format('YYYY-MM-DD')
                : '',
            status: this.doctorIncentiveTestingData.status
        };

        if (this.doctorIncentiveTestingData.mode === 'Edit') {
            params['id'] = this.doctorIncentiveTestingData.id;
        }

        this.doctorIncentiveTestingService
            .addUpdateDoctorIncentiveTesting(params)
            .then((res: any) => {
                this.isLoading = false;
                if (res && res.success) {
                    this.toasterService.pop({ type: 'success', title: 'Success', body: res.message });
                    this.doctorIncentiveTestingLoadEvent.emit();
                    this.bsModalRef.hide();
                } else {
                    this.toasterService.pop({ type: 'error', title: 'Error', body: res.message || 'Something went wrong' });
                }
            })
            .catch(() => {
                this.isLoading = false;
                this.toasterService.pop({ type: 'error', title: 'Error', body: 'Something went wrong' });
            });
    }

    saveExcelFile(): void {
        if (!this.excelData || !this.incentiveUploadList.length) {
            swal({ title: 'ERROR', text: 'Please upload an Excel file first', type: 'error' });
            return;
        }
        this.isLoading = true;
        const that = this;
        this.doctorIncentiveTestingService.uploadDoctorIncentiveTesting({ data: this.excelData })
            .then((res: any) => {
                that.isLoading = false;
                if (res.success) {
                    swal({ title: 'Success', html: that.buildResultHtml(res.message, res.errors), type: 'success' });
                    that.clearExcelData();
                    that.doctorIncentiveTestingLoadEvent.emit();
                    that.bsModalRef.hide();
                } else {
                    swal({ title: 'ERROR', html: that.buildResultHtml(res.message, res.errors), type: 'error' });
                }
            }).catch(() => {
                that.isLoading = false;
                swal({ title: 'ERROR', text: 'An error occurred while uploading', type: 'error' });
            });
    }
}



// WEBPACK FOOTER //
// /Applications/MAMP/htdocs/crm-soundsgood/src/app/components/master/doctorIncentiveTesting/addEditDoctorIncentiveTesting.component.ts